import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const COLORS = {
  gold: '#f0c040',
  cyan: '#00d4ff',
  green: '#00e87a',
  red: '#ff4444',
  purple: '#b060ff',
  amber: '#ffb040',
  bg: '#04060f',
  cardBg: '#0a1020',
  muted: '#4a5a7a',
};

function generateSlug(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\btopps\b/g, '')
    .replace(/\bstar wars\b/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const SUB_SET_TYPES = [
  { value: 'base', label: 'BASE', color: COLORS.green },
  { value: 'insert', label: 'INSERT', color: COLORS.cyan },
  { value: 'autograph', label: 'AUTOGRAPH', color: COLORS.purple },
  { value: 'sketch', label: 'SKETCH', color: COLORS.amber },
];

function typeMeta(value) {
  return SUB_SET_TYPES.find(t => t.value === value) || { label: value.toUpperCase(), color: COLORS.muted };
}

export default function EditSet({ user, slug }) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [set, setSet] = useState(null);
  const [name, setName] = useState('');
  const [year, setYear] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [section, setSection] = useState('');
  const [shortName, setShortName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [accentColor, setAccentColor] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [formError, setFormError] = useState(null);

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  // Sub-sets state
  const [subSets, setSubSets] = useState([]);
  const [showNewSubSetForm, setShowNewSubSetForm] = useState(false);
  const [newSSName, setNewSSName] = useState('');
  const [newSSType, setNewSSType] = useState('insert');
  const [newSSPrefix, setNewSSPrefix] = useState('');
  const [newSSTotal, setNewSSTotal] = useState('');
  const [newSSSlug, setNewSSSlug] = useState('');
  const [newSSSlugManuallyEdited, setNewSSSlugManuallyEdited] = useState(false);
  const [newSSSaving, setNewSSSaving] = useState(false);
  const [newSSError, setNewSSError] = useState(null);

  const [archivingSubSetId, setArchivingSubSetId] = useState(null);

  // PDF extraction state (Phase 1e.1)
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfProgressLabel, setPdfProgressLabel] = useState('');
  const [pdfResult, setPdfResult] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  // Phase 1e.2: review / edit / save state
  const [pdfReviewData, setPdfReviewData] = useState(null);  // mutable copy of pdfResult.data for editing
  const [expandedSubSetIndex, setExpandedSubSetIndex] = useState(null);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [pdfSaveResult, setPdfSaveResult] = useState(null);  // { created, skipped, errors }

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      try {
        const { data: adminRow, error: roleErr } = await supabase
          .from('admin_users')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (roleErr) throw roleErr;
        if (!mounted) return;

        if (!adminRow) {
          setRole(null);
          setLoading(false);
          return;
        }
        setRole(adminRow.role);

        const { data: setData, error: setErr } = await supabase
          .from('sets')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();

        if (setErr) throw setErr;
        if (!mounted) return;

        if (!setData) {
          setError(`Set with slug "${slug}" not found.`);
          setLoading(false);
          return;
        }

        setSet(setData);
        setName(setData.name);
        setYear(setData.year.toString());
        setManufacturer(setData.manufacturer);
        setSection(setData.section || '');
        setShortName(setData.short_name || '');
        setDescription(setData.description || '');
        setColor(setData.color || '');
        setAccentColor(setData.accent_color || '');

        await loadSubSets(setData.id, mounted);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Failed to load set');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [user.id, slug]);

  async function loadSubSets(setId, mounted = true) {
    const { data, error: err } = await supabase
      .from('sub_sets')
      .select('*, parallels!left(id, is_archived)')
      .eq('set_id', setId)
      .eq('is_archived', false)
      .order('ordering', { ascending: true })
      .order('name', { ascending: true });
    if (err) throw err;
    // Compute active parallel count per sub-set, then discard raw parallels array
    const withCounts = (data || []).map(ss => ({
      ...ss,
      parallel_count: (ss.parallels || []).filter(p => !p.is_archived).length,
    }));
    if (mounted) setSubSets(withCounts);
  }

  const backToList = () => {
    window.history.pushState({}, '', '/admin/checklists');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const goToSubSet = (subSetSlug) => {
    window.history.pushState({}, '', `/admin/checklists/edit/${slug}/subset/${subSetSlug}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  async function refetchSet() {
    const { data, error: err } = await supabase
      .from('sets')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (err) throw err;
    setSet(data);
    setName(data.name);
    setYear(data.year.toString());
    setManufacturer(data.manufacturer);
    setSection(data.section || '');
    setShortName(data.short_name || '');
    setDescription(data.description || '');
    setColor(data.color || '');
    setAccentColor(data.accent_color || '');
  }

  async function saveSet() {
    setFormError(null);
    setSaveMessage(null);

    if (!name.trim()) { setFormError('Name is required.'); return; }
    if (!year || isNaN(parseInt(year))) { setFormError('Year must be a valid number.'); return; }
    if (!manufacturer.trim()) { setFormError('Manufacturer is required.'); return; }

    setSaving(true);
    try {
      const { error: updateErr } = await supabase
        .from('sets')
        .update({
          name: name.trim(),
          year: parseInt(year),
          manufacturer: manufacturer.trim(),
          section: section || null,
          short_name: shortName.trim() || null,
          description: description.trim() || null,
          color: color.trim() || null,
          accent_color: accentColor.trim() || null,
        })
        .eq('id', set.id);

      if (updateErr) {
        setFormError(updateErr.message || 'Save failed.');
        setSaving(false);
        return;
      }

      await refetchSet();
      setSaveMessage('Saved.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function togglePublish() {
    setFormError(null);
    setSaving(true);
    try {
      const nextPublished = !set.is_published;
      const { error: updateErr } = await supabase
        .from('sets')
        .update({ is_published: nextPublished })
        .eq('id', set.id);

      if (updateErr) throw updateErr;
      await refetchSet();
      setSaveMessage(nextPublished ? 'Published.' : 'Unpublished.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Publish toggle failed.');
    } finally {
      setSaving(false);
    }
  }

  async function confirmArchive() {
    setFormError(null);
    setSaving(true);
    try {
      const { error: updateErr } = await supabase
        .from('sets')
        .update({ is_archived: true, is_published: false })
        .eq('id', set.id);

      if (updateErr) throw updateErr;
      backToList();
    } catch (err) {
      setFormError(err.message || 'Archive failed.');
      setSaving(false);
      setShowArchiveConfirm(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Sub-set form handlers
  // ---------------------------------------------------------------------------

  const openNewSubSetForm = () => {
    setNewSSName('');
    setNewSSType('insert');
    setNewSSPrefix('');
    setNewSSTotal('');
    setNewSSSlug('');
    setNewSSSlugManuallyEdited(false);
    setNewSSError(null);
    setShowNewSubSetForm(true);
  };

  const cancelNewSubSetForm = () => {
    setShowNewSubSetForm(false);
    setNewSSError(null);
  };

  const onSSNameChange = (e) => {
    const v = e.target.value;
    setNewSSName(v);
    if (!newSSSlugManuallyEdited) setNewSSSlug(generateSlug(v));
  };

  const onSSSlugChange = (e) => {
    setNewSSSlug(e.target.value);
    setNewSSSlugManuallyEdited(true);
  };

  const saveNewSubSet = async () => {
    setNewSSError(null);
    if (!newSSName.trim()) { setNewSSError('Name is required.'); return; }
    if (!newSSType) { setNewSSError('Type is required.'); return; }
    if (!newSSTotal || isNaN(parseInt(newSSTotal)) || parseInt(newSSTotal) <= 0) {
      setNewSSError('Total cards must be a positive number.'); return;
    }
    if (!newSSSlug.trim()) { setNewSSError('Slug is required.'); return; }
    if (!/^[a-z0-9-]+$/.test(newSSSlug)) {
      setNewSSError('Slug can only contain lowercase letters, numbers, and dashes.'); return;
    }

    setNewSSSaving(true);
    try {
      const nextOrdering = (subSets.reduce((max, s) => Math.max(max, s.ordering || 0), 0)) + 10;

      const { error: insertErr } = await supabase
        .from('sub_sets')
        .insert({
          set_id: set.id,
          slug: newSSSlug.trim(),
          name: newSSName.trim(),
          type: newSSType,
          card_number_prefix: newSSPrefix.trim() || null,
          total_cards: parseInt(newSSTotal),
          ordering: nextOrdering,
        });

      if (insertErr) {
        if (insertErr.code === '23505' || (insertErr.message && insertErr.message.includes('unique'))) {
          setNewSSError(`A sub-set with slug "${newSSSlug}" already exists in this set.`);
        } else {
          setNewSSError(insertErr.message || 'Save failed.');
        }
        setNewSSSaving(false);
        return;
      }

      await loadSubSets(set.id);
      setShowNewSubSetForm(false);
    } catch (err) {
      setNewSSError(err.message || 'Save failed.');
    } finally {
      setNewSSSaving(false);
    }
  };

  const beginArchiveSubSet = (id) => setArchivingSubSetId(id);
  const cancelArchiveSubSet = () => setArchivingSubSetId(null);

  const confirmArchiveSubSet = async (id) => {
    try {
      const { error: err } = await supabase
        .from('sub_sets')
        .update({ is_archived: true })
        .eq('id', id);
      if (err) throw err;
      await loadSubSets(set.id);
      setArchivingSubSetId(null);
    } catch (err) {
      alert('Archive failed: ' + (err.message || 'unknown'));
      setArchivingSubSetId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // PDF extraction (Phase 1e.1)
  // ---------------------------------------------------------------------------

  const handlePdfSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setPdfError('File must be a PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPdfError('PDF must be under 10MB.');
      return;
    }
    setPdfFile(file);
    setPdfError(null);
    setPdfResult(null);
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // result looks like "data:application/pdf;base64,JVBERi0..."
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const runExtraction = async () => {
    if (!pdfFile) return;
    setPdfUploading(true);
    setPdfError(null);
    setPdfResult(null);
    setPdfProgress(2);
    setPdfProgressLabel('Preparing PDF...');

    // Simulated progress driver
    let progressTimer = null;
    const startSimulation = () => {
      let pct = 10;
      setPdfProgress(10);
      setPdfProgressLabel('Sending to Claude...');
      progressTimer = setInterval(() => {
        pct += 2.5;
        if (pct >= 30 && pct < 35) setPdfProgressLabel('Claude is extracting...');
        if (pct >= 88) {
          pct = 88; // park until real response arrives
        }
        setPdfProgress(Math.min(pct, 88));
      }, 700);
    };

    try {
      // 0-10%: convert to base64
      const b64 = await fileToBase64(pdfFile);
      setPdfProgress(8);

      // 10-88%: call edge function (simulated)
      startSimulation();

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        throw new Error('Not authenticated.');
      }

      // Phase 3: kick off background extraction job, then poll for completion
      const resp = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/extract-checklist`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            pdf_base64: b64,
            set_context: {
              name: set.name,
              year: set.year,
              slug: set.slug,
            },
          }),
        }
      );

      const startBody = await resp.json();
      if (!resp.ok || !startBody.success || !startBody.job_id) {
        throw new Error(startBody?.error ? `${startBody.error}: ${startBody.details || 'see console'}` : `HTTP ${resp.status}`);
      }

      const jobId = startBody.job_id;
      if (progressTimer) clearInterval(progressTimer);
      setPdfProgress(40);
      setPdfProgressLabel('Extracting cards (this can take 1-3 min for big sets)...');

      // Poll the extraction_jobs table every 2s, up to 6 minutes
      const MAX_POLLS = 180; // 180 * 2s = 360s = 6 min
      let pollCount = 0;
      let jobRow = null;

      while (pollCount < MAX_POLLS) {
        await new Promise(r => setTimeout(r, 2000));
        pollCount++;

        const { data, error } = await supabase
          .from('extraction_jobs')
          .select('status, result, error_message, extraction_ms')
          .eq('id', jobId)
          .maybeSingle();

        if (error) {
          throw new Error(`Polling failed: ${error.message}`);
        }
        if (!data) {
          throw new Error('Job row not found — check Supabase logs');
        }

        // Animate progress 40 -> 90 while processing
        const pct = Math.min(90, 40 + Math.floor((pollCount / 60) * 50));
        setPdfProgress(pct);

        if (data.status === 'completed') {
          jobRow = data;
          break;
        }
        if (data.status === 'failed') {
          throw new Error(data.error_message || 'Extraction failed in background');
        }
      }

      if (!jobRow) {
        throw new Error('Extraction timed out after 6 minutes — try a smaller PDF');
      }

      setPdfProgress(95);
      setPdfProgressLabel('Parsing response...');

      const body = { success: true, data: jobRow.result, meta: { extraction_ms: jobRow.extraction_ms } };

      setPdfProgress(100);
      setPdfProgressLabel('Done');
      setPdfResult(body);
      setPdfReviewData(JSON.parse(JSON.stringify(body.data)));  // deep clone for editing
      setPdfSaveResult(null);
    } catch (err) {
      if (progressTimer) clearInterval(progressTimer);
      setPdfError(err.message || 'Extraction failed.');
      setPdfProgress(0);
      setPdfProgressLabel('');
    } finally {
      setTimeout(() => setPdfUploading(false), 300);
    }
  };

  const resetPdf = () => {
    setPdfFile(null);
    setPdfResult(null);
    setPdfError(null);
    setPdfProgress(0);
    setPdfProgressLabel('');
    setPdfReviewData(null);
    setExpandedSubSetIndex(null);
    setPdfSaveResult(null);
  };

  // ---------------------------------------------------------------------------
  // Review UI handlers (Phase 1e.2)
  // ---------------------------------------------------------------------------

  const updateReviewSubSet = (idx, field, value) => {
    setPdfReviewData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.sub_sets[idx][field] = value;
      return copy;
    });
  };

  const updateReviewCard = (subSetIdx, cardIdx, field, value) => {
    setPdfReviewData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.sub_sets[subSetIdx].cards[cardIdx][field] = value;
      return copy;
    });
  };

  const deleteReviewCard = (subSetIdx, cardIdx) => {
    setPdfReviewData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.sub_sets[subSetIdx].cards.splice(cardIdx, 1);
      copy.sub_sets[subSetIdx].total_cards = copy.sub_sets[subSetIdx].cards.length;
      return copy;
    });
  };

  const addReviewCard = (subSetIdx) => {
    setPdfReviewData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.sub_sets[subSetIdx].cards.push({ card_number: '', name: '', artist: null });
      copy.sub_sets[subSetIdx].total_cards = copy.sub_sets[subSetIdx].cards.length;
      return copy;
    });
  };

  const deleteReviewSubSet = (idx) => {
    if (!window.confirm('Remove this sub-set from the extraction? It will not be saved.')) return;
    setPdfReviewData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.sub_sets.splice(idx, 1);
      return copy;
    });
    if (expandedSubSetIndex === idx) setExpandedSubSetIndex(null);
  };

  // Normalize a name into a slug (for sub-set slugs that Claude doesn't provide)
  const slugifyForSubSet = (name) => {
    if (!name) return '';
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const saveAllToSet = async () => {
    if (!pdfReviewData || !pdfReviewData.sub_sets) return;
    if (!window.confirm(`Save ${pdfReviewData.sub_sets.length} sub-set(s) to this set? Existing sub-sets with the same slug will be skipped.`)) return;

    setPdfSaving(true);
    setPdfSaveResult(null);

    const result = { created_sub_sets: 0, created_cards: 0, skipped_sub_sets: [], errors: [] };

    // Load existing sub-sets so we can skip duplicates
    let existingSlugs = new Set();
    try {
      const { data: existing, error: exErr } = await supabase
        .from('sub_sets')
        .select('slug')
        .eq('set_id', set.id)
        .eq('is_archived', false);
      if (exErr) throw exErr;
      existingSlugs = new Set((existing || []).map(r => r.slug));
    } catch (err) {
      result.errors.push({ scope: 'preload', message: err.message || String(err) });
      setPdfSaveResult(result);
      setPdfSaving(false);
      return;
    }

    // Determine starting ordering for new sub-sets
    let nextOrdering = 0;
    try {
      const { data: maxRow } = await supabase
        .from('sub_sets')
        .select('ordering')
        .eq('set_id', set.id)
        .order('ordering', { ascending: false })
        .limit(1);
      nextOrdering = (maxRow && maxRow[0]?.ordering) ? maxRow[0].ordering + 10 : 10;
    } catch { nextOrdering = 10; }

    // Process each sub-set sequentially
    for (let i = 0; i < pdfReviewData.sub_sets.length; i++) {
      const ss = pdfReviewData.sub_sets[i];
      if (!ss.name || !ss.name.trim()) {
        result.errors.push({ scope: `sub_sets[${i}]`, message: 'Sub-set name is empty; skipped.' });
        continue;
      }

      const slug = slugifyForSubSet(ss.name);
      if (!slug) {
        result.errors.push({ scope: `sub_sets[${i}]`, message: `Could not derive slug from name "${ss.name}"; skipped.` });
        continue;
      }

      if (existingSlugs.has(slug)) {
        result.skipped_sub_sets.push(ss.name);
        continue;
      }

      // Validate type
      const validTypes = ['base', 'insert', 'autograph', 'sketch'];
      const safeType = validTypes.includes(ss.type) ? ss.type : 'insert';

      // Insert sub-set
      const { data: newSub, error: subErr } = await supabase
        .from('sub_sets')
        .insert({
          set_id: set.id,
          slug,
          name: ss.name.trim(),
          type: safeType,
          card_number_prefix: (ss.card_number_prefix && ss.card_number_prefix.trim()) ? ss.card_number_prefix.trim() : null,
          total_cards: ss.cards ? ss.cards.length : (ss.total_cards || 0),
          ordering: nextOrdering,
        })
        .select('id')
        .maybeSingle();

      nextOrdering += 10;

      if (subErr || !newSub) {
        result.errors.push({ scope: ss.name, message: subErr?.message || 'Insert failed.' });
        continue;
      }

      result.created_sub_sets++;

      // Insert cards in batches
      if (ss.cards && ss.cards.length > 0) {
        const cardRows = ss.cards
          .filter(c => c.card_number && c.name && c.card_number.trim() && c.name.trim())
          .map((c, cardIdx) => ({
            sub_set_id: newSub.id,
            card_number: c.card_number.trim(),
            name: c.name.trim(),
            artist: c.artist && c.artist.trim() ? c.artist.trim() : null,
            ordering: cardIdx * 10,
          }));

        // Batch in groups of 200 to avoid Supabase payload limits
        const BATCH_SIZE = 200;
        for (let b = 0; b < cardRows.length; b += BATCH_SIZE) {
          const batch = cardRows.slice(b, b + BATCH_SIZE);
          const { error: cardsErr } = await supabase.from('cards').insert(batch);
          if (cardsErr) {
            result.errors.push({ scope: `${ss.name} cards batch ${b / BATCH_SIZE + 1}`, message: cardsErr.message });
          } else {
            result.created_cards += batch.length;
          }
        }
      }

      existingSlugs.add(slug);
    }

    setPdfSaveResult(result);
    setPdfSaving(false);

    // Refresh the set's sub-sets list so the user sees the new data immediately
    try {
      await loadSubSets(set.id);
    } catch { /* ignore */ }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace', fontSize: 13 }}>
          Loading set...
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>EDIT SET</div>
          <div style={{ color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 16, marginTop: 24 }}>
            You don't have admin access.
          </div>
          <button onClick={backToList} style={{ ...buttonOutlineStyle, marginTop: 20 }}>BACK TO LIST</button>
        </div>
      </div>
    );
  }

  if (error || !set) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>EDIT SET</div>
          <div style={{ color: COLORS.red, fontFamily: 'Share Tech Mono, monospace', fontSize: 13, marginTop: 24 }}>
            {error || 'Set not found.'}
          </div>
          <button onClick={backToList} style={{ ...buttonOutlineStyle, marginTop: 20 }}>BACK TO LIST</button>
        </div>
      </div>
    );
  }

  const isOwner = role === 'owner';

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={headerTitle}>EDIT SET</div>
          <div style={{
            fontFamily: 'Share Tech Mono, monospace',
            color: COLORS.green,
            fontSize: 11,
            letterSpacing: 2,
          }}>
            {(typeof window !== 'undefined' && window.location.hostname === 'collectionkeeper.org') ? 'PRODUCTION' : 'STAGING'} · {role.toUpperCase()}
          </div>
        </div>

        <div style={{
          fontFamily: 'Rajdhani, sans-serif',
          color: COLORS.muted,
          fontSize: 14,
          marginBottom: 20,
        }}>
          Editing <span style={{ color: COLORS.cyan, fontFamily: 'Share Tech Mono, monospace' }}>{set.slug}</span>
        </div>

        <div style={{ marginBottom: 20 }}>
          <button onClick={backToList} style={buttonOutlineStyle}>← BACK TO LIST</button>
        </div>

        {/* Main edit card — set fields */}
        <div style={{
          background: COLORS.cardBg,
          borderRadius: 6,
          padding: 20,
          border: `1px solid ${COLORS.muted}22`,
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
            {set.is_published ? (
              <span style={{
                color: COLORS.green, fontSize: 11, fontFamily: 'Share Tech Mono, monospace',
                letterSpacing: 1.5, padding: '4px 10px',
                border: `1px solid ${COLORS.green}`, borderRadius: 4,
              }}>● PUBLISHED</span>
            ) : (
              <span style={{
                color: COLORS.gold, fontSize: 11, fontFamily: 'Share Tech Mono, monospace',
                letterSpacing: 1.5, padding: '4px 10px',
                border: `1px solid ${COLORS.gold}`, borderRadius: 4,
              }}>◯ DRAFT</span>
            )}
            {saveMessage && (
              <span style={{
                color: COLORS.green, fontSize: 12, fontFamily: 'Share Tech Mono, monospace',
                marginLeft: 8,
              }}>
                ✓ {saveMessage}
              </span>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={formLabelStyle}>SLUG (READ-ONLY)</label>
            <input
              type="text"
              value={set.slug}
              disabled
              style={{
                ...formInputStyle,
                color: COLORS.muted,
                cursor: 'not-allowed',
                background: '#020408',
              }}
            />
            <div style={{
              fontFamily: 'Share Tech Mono, monospace',
              color: COLORS.muted, fontSize: 11, marginTop: 4,
            }}>
              Slug cannot be changed after creation to protect existing user data references.
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={formLabelStyle}>NAME</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={formInputStyle}
              disabled={!isOwner || saving}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={formLabelStyle}>YEAR</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                style={formInputStyle}
                disabled={!isOwner || saving}
              />
            </div>
            <div>
              <label style={formLabelStyle}>MANUFACTURER</label>
              <input
                type="text"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                style={formInputStyle}
                disabled={!isOwner || saving}
              />
            </div>
            <div>
              <label style={formLabelStyle}>SECTION</label>
              <select
                value={section}
                onChange={(e) => setSection(e.target.value)}
                style={formInputStyle}
                disabled={!isOwner || saving}
              >
                <option value="">(none)</option>
                <option value="chrome-black">Chrome Black</option>
                <option value="chrome-sw">Chrome Star Wars</option>
                <option value="smugglers-outpost">Smuggler's Outpost</option>
                <option value="galaxy">Chrome Galaxy</option>
              </select>
            </div>
            <div>
              <label style={formLabelStyle}>SHORT NAME</label>
              <input
                type="text"
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                placeholder="e.g. Chrome Galaxy 2024"
                style={formInputStyle}
                disabled={!isOwner || saving}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={formLabelStyle}>DESCRIPTION</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Marketing blurb shown on the My Checklists tile"
                style={{ ...formInputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                disabled={!isOwner || saving}
              />
            </div>
            <div>
              <label style={formLabelStyle}>COLOR (HEX)</label>
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#f0c040"
                style={formInputStyle}
                disabled={!isOwner || saving}
              />
            </div>
            <div>
              <label style={formLabelStyle}>ACCENT COLOR (HEX)</label>
              <input
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#c9a227"
                style={formInputStyle}
                disabled={!isOwner || saving}
              />
            </div>
          </div>

          {formError && (
            <div style={{
              color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
              padding: 8, marginTop: 8, marginBottom: 8,
              background: `${COLORS.red}15`, borderRadius: 4,
            }}>
              {formError}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button
              onClick={saveSet}
              disabled={!isOwner || saving}
              style={{ ...buttonPrimaryStyle, opacity: (!isOwner || saving) ? 0.6 : 1, cursor: (!isOwner || saving) ? 'not-allowed' : 'pointer' }}
            >
              {saving ? 'SAVING...' : 'SAVE'}
            </button>
          </div>

          {!isOwner && (
            <div style={{
              fontFamily: 'Share Tech Mono, monospace',
              color: COLORS.muted, fontSize: 11, marginTop: 10,
            }}>
              Only the owner can edit set-level fields. Super users edit sub-sets they've been granted.
            </div>
          )}
        </div>

        {/* Publish/Archive */}
        {isOwner && (
          <div style={{
            background: COLORS.cardBg, borderRadius: 6, padding: 20,
            border: `1px solid ${COLORS.muted}22`, marginBottom: 16,
          }}>
            <div style={{
              fontFamily: 'Orbitron, sans-serif', color: COLORS.gold,
              fontSize: 12, letterSpacing: 1.5, marginBottom: 14,
            }}>
              DANGER ZONE
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${COLORS.muted}22` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {set.is_published ? 'Unpublish this set' : 'Publish this set'}
                </div>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 11 }}>
                  {set.is_published
                    ? 'Making it a draft again will hide it from regular users.'
                    : 'Making it published will show it to all users.'}
                </div>
              </div>
              <button
                onClick={togglePublish}
                disabled={saving}
                style={set.is_published
                  ? { ...buttonOutlineStyle, color: COLORS.gold, borderColor: COLORS.gold }
                  : { ...buttonPrimaryStyle, background: COLORS.green, color: COLORS.bg }
                }
              >
                {set.is_published ? 'UNPUBLISH' : 'PUBLISH'}
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Archive this set</div>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 11 }}>
                  Removes the set from the list. Recoverable via SQL. Safer than delete.
                </div>
              </div>
              {!showArchiveConfirm ? (
                <button
                  onClick={() => setShowArchiveConfirm(true)}
                  disabled={saving}
                  style={{ ...buttonOutlineStyle, color: COLORS.red, borderColor: COLORS.red }}
                >
                  ARCHIVE
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={confirmArchive}
                    disabled={saving}
                    style={{ ...buttonPrimaryStyle, background: COLORS.red, color: '#fff', fontSize: 11, padding: '8px 12px' }}
                  >
                    {saving ? 'ARCHIVING...' : 'CONFIRM ARCHIVE'}
                  </button>
                  <button
                    onClick={() => setShowArchiveConfirm(false)}
                    disabled={saving}
                    style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted, fontSize: 11, padding: '8px 12px' }}
                  >
                    CANCEL
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sub-sets section */}
        <div style={{
          background: COLORS.cardBg, borderRadius: 6, padding: 20,
          border: `1px solid ${COLORS.muted}22`, marginBottom: 16,
        }}>
          <div style={{
            fontFamily: 'Orbitron, sans-serif', color: COLORS.gold,
            fontSize: 12, letterSpacing: 1.5, marginBottom: 4,
          }}>
            SUB-SETS
          </div>
          <div style={{ fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 12, marginBottom: 14 }}>
            Base, inserts, autographs, sketches. Each sub-set has its own cards and parallels.
          </div>

          {/* New sub-set form */}
          {isOwner && !showNewSubSetForm && (
            <div style={{ marginBottom: 14 }}>
              <button onClick={openNewSubSetForm} style={buttonPrimaryStyle}>+ ADD SUB-SET</button>
            </div>
          )}

          {showNewSubSetForm && (
            <div style={{
              border: `1px solid ${COLORS.cyan}`, borderRadius: 4,
              padding: 14, marginBottom: 14, background: '#020408',
            }}>
              <div style={{
                fontFamily: 'Orbitron, sans-serif', color: COLORS.cyan,
                fontSize: 11, letterSpacing: 1.5, marginBottom: 10,
              }}>
                NEW SUB-SET
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={formLabelStyle}>NAME</label>
                  <input
                    type="text" value={newSSName} onChange={onSSNameChange}
                    style={formInputStyle} placeholder="e.g. Power of the Force" autoFocus
                  />
                </div>
                <div>
                  <label style={formLabelStyle}>TYPE</label>
                  <select
                    value={newSSType} onChange={(e) => setNewSSType(e.target.value)}
                    style={formInputStyle}
                  >
                    {SUB_SET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={formLabelStyle}>SLUG</label>
                  <input
                    type="text" value={newSSSlug} onChange={onSSSlugChange}
                    style={formInputStyle} placeholder="e.g. power-force"
                  />
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 10, marginTop: 2 }}>
                    {newSSSlugManuallyEdited ? 'Manually edited.' : 'Auto-generated.'}
                  </div>
                </div>
                <div>
                  <label style={formLabelStyle}>PREFIX</label>
                  <input
                    type="text" value={newSSPrefix} onChange={(e) => setNewSSPrefix(e.target.value)}
                    style={formInputStyle} placeholder="e.g. POW-"
                  />
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 10, marginTop: 2 }}>
                    Optional.
                  </div>
                </div>
                <div>
                  <label style={formLabelStyle}>TOTAL CARDS</label>
                  <input
                    type="number" value={newSSTotal} onChange={(e) => setNewSSTotal(e.target.value)}
                    style={formInputStyle} placeholder="15"
                  />
                </div>
              </div>

              {newSSError && (
                <div style={{
                  color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
                  padding: 8, marginBottom: 10, background: `${COLORS.red}15`, borderRadius: 4,
                }}>
                  {newSSError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  onClick={saveNewSubSet} disabled={newSSSaving}
                  style={{ ...buttonPrimaryStyle, opacity: newSSSaving ? 0.6 : 1, cursor: newSSSaving ? 'not-allowed' : 'pointer' }}
                >
                  {newSSSaving ? 'SAVING...' : 'SAVE SUB-SET'}
                </button>
                <button
                  onClick={cancelNewSubSetForm} disabled={newSSSaving}
                  style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          )}

          {/* Sub-sets list */}
          <div style={{ border: `1px solid ${COLORS.muted}22`, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 100px 80px 70px 90px 160px',
              padding: '8px 12px', background: COLORS.bg,
              fontFamily: 'Orbitron, sans-serif', fontSize: 10, color: COLORS.gold, letterSpacing: 1,
            }}>
              <div>NAME</div>
              <div>TYPE</div>
              <div>PREFIX</div>
              <div>CARDS</div>
              <div>PARALLELS</div>
              <div>ACTIONS</div>
            </div>

            {subSets.length === 0 ? (
              <div style={{
                padding: '24px 12px', textAlign: 'center',
                fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 13,
              }}>
                No sub-sets yet. Click <span style={{ color: COLORS.cyan }}>+ ADD SUB-SET</span> to create one.
              </div>
            ) : (
              subSets.map(ss => {
                const meta = typeMeta(ss.type);
                return (
                  <div key={ss.id} style={{
                    display: 'grid', gridTemplateColumns: '2fr 100px 80px 70px 90px 160px',
                    padding: '10px 12px', borderTop: `1px solid ${COLORS.muted}22`,
                    fontFamily: 'Rajdhani, sans-serif', fontSize: 13, alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{ss.name}</div>
                      <div style={{ fontSize: 10, color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace' }}>{ss.slug}</div>
                    </div>
                    <div>
                      <span style={{
                        color: meta.color, border: `1px solid ${meta.color}`,
                        background: `${meta.color}15`, padding: '2px 8px', borderRadius: 3,
                        fontFamily: 'Share Tech Mono, monospace', fontSize: 10, letterSpacing: 1,
                      }}>{meta.label}</span>
                    </div>
                    <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 11 }}>
                      {ss.card_number_prefix || <span style={{ color: COLORS.muted }}>—</span>}
                    </div>
                    <div>{ss.total_cards}</div>
                    <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 12, color: (ss.parallel_count || 0) === 0 ? COLORS.muted : COLORS.cyan }}>
                      {ss.parallel_count || 0}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => goToSubSet(ss.slug)}
                        style={{ ...buttonOutlineStyle, padding: '3px 8px', fontSize: 10 }}
                      >
                        EDIT
                      </button>
                      {isOwner && archivingSubSetId !== ss.id && (
                        <button
                          onClick={() => beginArchiveSubSet(ss.id)}
                          style={{ ...buttonOutlineStyle, padding: '3px 8px', fontSize: 10, color: COLORS.red, borderColor: COLORS.red }}
                        >
                          ARCHIVE
                        </button>
                      )}
                      {isOwner && archivingSubSetId === ss.id && (
                        <>
                          <button
                            onClick={() => confirmArchiveSubSet(ss.id)}
                            style={{ padding: '3px 6px', fontSize: 10, background: COLORS.red, color: '#fff', border: 'none', borderRadius: 4, fontFamily: 'Orbitron, sans-serif', letterSpacing: 1, cursor: 'pointer' }}
                          >
                            CONFIRM
                          </button>
                          <button
                            onClick={cancelArchiveSubSet}
                            style={{ padding: '3px 6px', fontSize: 10, background: 'transparent', color: COLORS.muted, border: `1px solid ${COLORS.muted}`, borderRadius: 4, fontFamily: 'Orbitron, sans-serif', letterSpacing: 1, cursor: 'pointer' }}
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* PDF extraction section (Phase 1e.1) */}
        {isOwner && (
          <div style={{
            background: COLORS.cardBg, borderRadius: 6, padding: 20,
            border: `1px solid ${COLORS.muted}22`, marginBottom: 16,
          }}>
            <div style={{
              fontFamily: 'Orbitron, sans-serif', color: COLORS.gold,
              fontSize: 12, letterSpacing: 1.5, marginBottom: 4,
            }}>
              EXTRACT CHECKLIST FROM PDF
            </div>
            <div style={{ fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 12, marginBottom: 14 }}>
              Upload the official Topps release checklist. Claude will extract sub-sets, cards, and parallels. Phase 1e.1 shows raw JSON only — review/save UI comes in 1e.2.
            </div>

            {!pdfFile && !pdfResult && (
              <div>
                <input
                  id="checklistPdfInput"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handlePdfSelected}
                  style={{ display: 'none' }}
                />
                <label htmlFor="checklistPdfInput" style={{ ...buttonPrimaryStyle, display: 'inline-block' }}>
                  CHOOSE PDF
                </label>
              </div>
            )}

            {pdfFile && !pdfUploading && !pdfResult && (
              <div>
                <div style={{
                  padding: 10, background: '#020408', borderRadius: 4,
                  fontFamily: 'Share Tech Mono, monospace', fontSize: 12,
                  color: COLORS.cyan, marginBottom: 12,
                }}>
                  Selected: {pdfFile.name} <span style={{ color: COLORS.muted }}>({Math.round(pdfFile.size / 1024)} KB)</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={runExtraction} style={buttonPrimaryStyle}>EXTRACT WITH CLAUDE</button>
                  <button onClick={resetPdf} style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}>CANCEL</button>
                </div>
              </div>
            )}

            {pdfUploading && (
              <div>
                <div style={{
                  padding: 10, background: '#020408', borderRadius: 4,
                  fontFamily: 'Share Tech Mono, monospace', fontSize: 12,
                  color: COLORS.cyan, marginBottom: 12,
                }}>
                  {pdfFile?.name}
                </div>
                <div style={{
                  fontFamily: 'Share Tech Mono, monospace',
                  color: COLORS.muted, fontSize: 11, marginBottom: 6,
                }}>
                  {pdfProgressLabel} {Math.round(pdfProgress)}%
                </div>
                <div style={{
                  width: '100%', height: 8, background: '#020408',
                  borderRadius: 4, overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${pdfProgress}%`, height: '100%',
                    background: COLORS.cyan, transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            )}

            {pdfError && (
              <div style={{
                color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
                padding: 10, marginTop: 10, background: `${COLORS.red}15`, borderRadius: 4,
              }}>
                Error: {pdfError}
                <button onClick={resetPdf} style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted, marginLeft: 10, padding: '4px 10px', fontSize: 11 }}>TRY AGAIN</button>
              </div>
            )}

            {pdfResult && pdfReviewData && !pdfSaveResult && (
              <div>
                <div style={{
                  padding: 10, background: '#020408', borderRadius: 4,
                  fontFamily: 'Share Tech Mono, monospace', fontSize: 11,
                  color: COLORS.green, marginBottom: 10,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span>✓ Extracted in {Math.round((pdfResult.meta?.extraction_ms || 0) / 100) / 10}s · {pdfReviewData.sub_sets?.length || 0} sub-sets · {(pdfReviewData.sub_sets || []).reduce((sum, s) => sum + (s.cards?.length || 0), 0)} cards</span>
                  <span style={{ color: COLORS.muted, fontSize: 10 }}>Nothing saved yet.</span>
                </div>

                <div style={{
                  fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted,
                  fontSize: 11, marginBottom: 12,
                }}>
                  Review and edit below. Parallels are not extracted from Topps PDFs — add them manually after saving.
                </div>

                {/* Sub-sets preview */}
                {pdfReviewData.sub_sets && pdfReviewData.sub_sets.map((ss, ssIdx) => {
                  const meta = (function(t) {
                    const types = [
                      { value: 'base', label: 'BASE', color: COLORS.green },
                      { value: 'insert', label: 'INSERT', color: COLORS.cyan },
                      { value: 'autograph', label: 'AUTOGRAPH', color: COLORS.purple },
                      { value: 'sketch', label: 'SKETCH', color: COLORS.amber },
                    ];
                    return types.find(x => x.value === t) || { label: (t || '').toUpperCase(), color: COLORS.muted };
                  })(ss.type);

                  const isExpanded = expandedSubSetIndex === ssIdx;

                  return (
                    <div key={ssIdx} style={{
                      background: '#020408',
                      border: `1px solid ${COLORS.muted}33`,
                      borderRadius: 4, marginBottom: 10,
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: 10, borderBottom: isExpanded ? `1px solid ${COLORS.muted}22` : 'none',
                      }}>
                        <input
                          type="text" value={ss.name || ''}
                          onChange={(e) => updateReviewSubSet(ssIdx, 'name', e.target.value)}
                          style={{ ...formInputStyle, fontSize: 13, fontFamily: 'Rajdhani, sans-serif', fontWeight: 500, flex: '0 0 220px' }}
                        />
                        <select
                          value={ss.type || 'insert'}
                          onChange={(e) => updateReviewSubSet(ssIdx, 'type', e.target.value)}
                          style={{ ...formInputStyle, width: 120, fontSize: 11 }}
                        >
                          <option value="base">BASE</option>
                          <option value="insert">INSERT</option>
                          <option value="autograph">AUTOGRAPH</option>
                          <option value="sketch">SKETCH</option>
                        </select>
                        <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 10, color: COLORS.muted }}>prefix:</span>
                        <input
                          type="text" value={ss.card_number_prefix || ''}
                          onChange={(e) => updateReviewSubSet(ssIdx, 'card_number_prefix', e.target.value)}
                          style={{ ...formInputStyle, width: 80, fontSize: 11 }}
                          placeholder="—"
                        />
                        <span style={{ color: COLORS.green, fontFamily: 'Share Tech Mono, monospace', fontSize: 11 }}>
                          {ss.cards?.length || 0} cards
                        </span>
                        <div style={{ flex: 1 }} />
                        <button
                          onClick={() => setExpandedSubSetIndex(isExpanded ? null : ssIdx)}
                          style={{ ...buttonOutlineStyle, padding: '4px 10px', fontSize: 10, color: COLORS.muted, borderColor: COLORS.muted }}
                        >
                          {isExpanded ? '▲ HIDE' : '▼ SHOW'} CARDS
                        </button>
                        <button
                          onClick={() => deleteReviewSubSet(ssIdx)}
                          style={{ ...buttonOutlineStyle, padding: '4px 10px', fontSize: 10, color: COLORS.red, borderColor: COLORS.red }}
                          title="Remove from extraction"
                        >
                          ✕
                        </button>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: 10 }}>
                          <div style={{
                            display: 'grid', gridTemplateColumns: '80px 1fr 1fr 30px',
                            gap: 6, paddingBottom: 6, borderBottom: `1px solid ${COLORS.muted}22`,
                            fontFamily: 'Orbitron, sans-serif', fontSize: 9,
                            color: COLORS.gold, letterSpacing: 1,
                          }}>
                            <div>CARD #</div>
                            <div>NAME</div>
                            <div>ARTIST</div>
                            <div></div>
                          </div>
                          {(ss.cards || []).map((card, cardIdx) => (
                            <div key={cardIdx} style={{
                              display: 'grid', gridTemplateColumns: '80px 1fr 1fr 30px',
                              gap: 6, padding: '4px 0',
                              borderBottom: `1px solid ${COLORS.muted}15`, alignItems: 'center',
                            }}>
                              <input
                                type="text" value={card.card_number || ''}
                                onChange={(e) => updateReviewCard(ssIdx, cardIdx, 'card_number', e.target.value)}
                                style={{ ...formInputStyle, padding: '4px 6px', fontSize: 11 }}
                              />
                              <input
                                type="text" value={card.name || ''}
                                onChange={(e) => updateReviewCard(ssIdx, cardIdx, 'name', e.target.value)}
                                style={{ ...formInputStyle, padding: '4px 6px', fontSize: 11 }}
                              />
                              <input
                                type="text" value={card.artist || ''}
                                onChange={(e) => updateReviewCard(ssIdx, cardIdx, 'artist', e.target.value || null)}
                                style={{ ...formInputStyle, padding: '4px 6px', fontSize: 11 }}
                                placeholder="—"
                              />
                              <div style={{
                                textAlign: 'center', color: COLORS.red, cursor: 'pointer',
                                fontFamily: 'Share Tech Mono, monospace', fontSize: 14,
                              }} onClick={() => deleteReviewCard(ssIdx, cardIdx)} title="Delete row">
                                ✕
                              </div>
                            </div>
                          ))}
                          <div
                            onClick={() => addReviewCard(ssIdx)}
                            style={{
                              padding: '6px 0', textAlign: 'center', cursor: 'pointer',
                              fontFamily: 'Share Tech Mono, monospace', fontSize: 11,
                              color: COLORS.cyan,
                            }}
                          >
                            + add row
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {(!pdfReviewData.sub_sets || pdfReviewData.sub_sets.length === 0) && (
                  <div style={{
                    padding: 16, textAlign: 'center', background: '#020408', borderRadius: 4,
                    fontFamily: 'Rajdhani, sans-serif', color: COLORS.muted, fontSize: 13,
                  }}>
                    No sub-sets left to save. Upload another PDF or cancel.
                  </div>
                )}

                {/* Action footer */}
                <div style={{
                  marginTop: 16, paddingTop: 14,
                  borderTop: `1px solid ${COLORS.muted}22`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 11 }}>
                    Review complete above. Nothing is saved until you confirm.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={resetPdf} style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }} disabled={pdfSaving}>
                      DISCARD
                    </button>
                    <button
                      onClick={saveAllToSet} disabled={pdfSaving || !pdfReviewData.sub_sets || pdfReviewData.sub_sets.length === 0}
                      style={{
                        ...buttonPrimaryStyle, background: COLORS.green, color: COLORS.bg,
                        opacity: pdfSaving ? 0.6 : 1,
                        cursor: pdfSaving ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {pdfSaving ? 'SAVING...' : '✓ SAVE ALL TO SET'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {pdfSaveResult && (
              <div style={{
                padding: 14, background: '#020408', borderRadius: 4,
                border: `1px solid ${(pdfSaveResult.errors || []).length > 0 ? COLORS.amber : COLORS.green}`,
                marginTop: 10,
              }}>
                <div style={{
                  fontFamily: 'Orbitron, sans-serif', fontSize: 12, letterSpacing: 1.5,
                  color: (pdfSaveResult.errors || []).length > 0 ? COLORS.amber : COLORS.green,
                  marginBottom: 10,
                }}>
                  {(pdfSaveResult.errors || []).length > 0 ? 'SAVED WITH ERRORS' : 'SAVED'}
                </div>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 13, lineHeight: 1.6, color: '#fff' }}>
                  <div>Created <strong style={{ color: COLORS.green }}>{pdfSaveResult.created_sub_sets}</strong> sub-set{pdfSaveResult.created_sub_sets === 1 ? '' : 's'} with <strong style={{ color: COLORS.green }}>{pdfSaveResult.created_cards}</strong> card{pdfSaveResult.created_cards === 1 ? '' : 's'}.</div>
                  {pdfSaveResult.skipped_sub_sets.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      Skipped <strong style={{ color: COLORS.muted }}>{pdfSaveResult.skipped_sub_sets.length}</strong> existing: {pdfSaveResult.skipped_sub_sets.join(', ')}
                    </div>
                  )}
                  {pdfSaveResult.errors.length > 0 && (
                    <div style={{
                      marginTop: 10, padding: 10,
                      background: `${COLORS.red}15`, borderRadius: 4,
                      fontFamily: 'Share Tech Mono, monospace', fontSize: 11, color: COLORS.red,
                    }}>
                      <div style={{ marginBottom: 4, fontWeight: 700 }}>Errors:</div>
                      {pdfSaveResult.errors.map((e, i) => (
                        <div key={i}>• {e.scope}: {e.message}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  <button onClick={resetPdf} style={buttonPrimaryStyle}>DONE · UPLOAD ANOTHER</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{
          marginTop: 16, padding: 12, background: COLORS.cardBg, borderRadius: 4,
          fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 11, lineHeight: 1.6,
        }}>
          Phase 1e.1: PDF extraction returns raw JSON only. Review UI + save-to-tables coming in 1e.2.
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared styles
// -----------------------------------------------------------------------------

const containerStyle = {
  minHeight: '100vh', background: COLORS.bg,
  padding: '24px 16px', fontFamily: 'Rajdhani, sans-serif', color: '#fff',
};
const headerTitle = {
  fontFamily: 'Orbitron, sans-serif', color: COLORS.gold,
  fontSize: 20, letterSpacing: 1,
};
const buttonPrimaryStyle = {
  padding: '10px 18px', background: COLORS.gold, color: COLORS.bg, border: 'none',
  borderRadius: 4, fontFamily: 'Orbitron, sans-serif', fontSize: 12,
  letterSpacing: 1.5, fontWeight: 700, cursor: 'pointer',
};
const buttonOutlineStyle = {
  padding: '10px 18px', background: 'transparent', color: COLORS.cyan,
  border: `1px solid ${COLORS.cyan}`, borderRadius: 4,
  fontFamily: 'Orbitron, sans-serif', fontSize: 12, letterSpacing: 1.5, cursor: 'pointer',
};
const formLabelStyle = {
  fontFamily: 'Orbitron, sans-serif', fontSize: 10,
  color: COLORS.gold, letterSpacing: 1, marginBottom: 4, display: 'block',
};
const formInputStyle = {
  width: '100%', background: COLORS.bg, color: '#fff',
  border: `1px solid ${COLORS.muted}66`, borderRadius: 4,
  padding: '8px 10px', fontFamily: 'Share Tech Mono, monospace',
  fontSize: 13, boxSizing: 'border-box',
};
